import { Box, Button } from "@mui/material";
import axios from "axios";
import { useContext, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../contexts/AuthContext";
import { CONFIG } from "../../services/config";

function SortableCard({ item }: { item: any }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.pillsOrderingId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        border: "1px solid var(--gray-300)",
        borderRadius: 2,
        overflow: "hidden",
        cursor: "grab",
        userSelect: "none",
        backgroundColor: "white",
      }}
    >
      <Box
        sx={{
          position: "relative",
          transitionDuration: "300ms",
          minHeight: 150,
          display: "flex",
          placeItems: "center",
          overflow: "hidden",
          borderBottom: "1px solid var(--gray-100)",
        }}
      >
        <img
          src={item.pillVehicleId.pillsDomain.thumbId}
          alt={item.title}
          width="100%"
          style={{
            borderTopRightRadius: 8,
            borderTopLeftRadius: 8,
            maxHeight: 150,
          }}
        />
      </Box>
      <Box sx={{ paddingX: 2, backgroundColor: "white" }}>
        <p style={{ fontSize: 14, fontWeight: "bold" }}>
          {item.pillVehicleId.pillsDomain.title}
        </p>
        <p
          style={{
            fontSize: 13,
            color: "var(--gray-300)",
            whiteSpace: "normal",
            width: "100%",
            height: "3em",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.pillVehicleId.pillsDomain.description}
        </p>
      </Box>
      <Box
        sx={{
          paddingX: 2,
          display: "flex",
          rowGap: 1,
          columnGap: 3,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            color: "white",
            borderRadius: 5,
            paddingX: 1,
            paddingY: 0.5,
            fontWeight: "bold",
            fontSize: 10,
            backgroundColor: "var(--blue-400)",
            display: "flex",
            placeItems: "center",
            marginBottom: 2,
          }}
        >
          {item.vehicleId.name} | {item.vehicleVersionId.name}
        </Box>
      </Box>
    </Box>
  );
}

export default function VehiclePills({
  data,
  messageError,
  watchDataChange,
}: any) {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);

  const [items, setItems] = useState<any[]>(data);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.pillsOrderingId === active.id);
    const newIndex = items.findIndex((it) => it.pillsOrderingId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setItems(arrayMove(items, oldIndex, newIndex));
  };

  const handleSaveOrder = async () => {
    const newOrder = items.map((item, i) => ({
      pillsOrderingId: item.pillsOrderingId,
      orderingNo: i,
    }));

    try {
      axios.put(
        `${CONFIG.API_URL}pills/ordering`,
        { orderList: newOrder },
        tokenHeaders,
      );
    } catch (error: any) {
      messageError(error.response.data.message);
    }
    watchDataChange(t("pills.orderSuccess"));
  };

  return (
    <Box sx={{ borderTop: "1px solid var(--gray-300)" }}>
      <Box sx={{ marginBottom: 4 }}>
        <Box sx={{ marginTop: 2 }}>
          <span>{t("pills.instructions1")}</span>
          <br />
          <span>{t("pills.instructions2")}</span>
        </Box>
      </Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((it) => it.pillsOrderingId)}
          strategy={rectSortingStrategy}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 2,
              marginBottom: 3,
            }}
          >
            {items.map((item: any) => (
              <SortableCard key={item.pillsOrderingId} item={item} />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
      <Button variant="contained" onClick={handleSaveOrder}>
        {t("pills.save")}
      </Button>
    </Box>
  );
}
